/**
 * Metabolic Quiz - State Machine
 * Controls quiz flow, state management, and user interactions
 */

(function() {
  'use strict';

  // State object
  const quizState = {
    currentQuestion: 0,
    totalQuestions: 0,
    responses: [],
    email: null,
    gender: null, // 'male' or 'female'
    isTransitioning: false,
    startTime: null,
    selectedAnswers: [], // For multiple choice questions
    completed: false // Flag to indicate quiz completion
  };

  // DOM elements
  let elements = {};

  // Initialize quiz when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuiz);
  } else {
    initQuiz();
  }

  /**
   * Initialize the quiz
   */
  function initQuiz() {
    // Check if quiz config exists
    if (typeof window.quizConfig === 'undefined') {
      console.error('Quiz configuration not found');
      return;
    }

    // Cache DOM elements
    cacheElements();

    // Set total questions (count unique question IDs, not total array length)
    // This handles cases where we have multiple versions of same question (e.g., Q6 male/female)
    const uniqueQuestionIds = [...new Set(window.quizConfig.questions.map(q => q.id))];
    quizState.totalQuestions = uniqueQuestionIds.length;

    // Try to load saved state
    loadState();

    // Setup event listeners
    setupEventListeners();

    // Set viewport height for iOS
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
  }

  /**
   * Cache DOM elements
   */
  function cacheElements() {
    elements = {
      container: document.getElementById('quizContainer'),
      welcomeScreen: document.getElementById('welcomeScreen'),
      questionScreen: document.getElementById('questionScreen'),
      loadingScreen: document.getElementById('loadingScreen'),
      emailScreen: document.getElementById('emailScreen'),
      resultsScreen: document.getElementById('resultsScreen'),
      genderBtnMale: document.getElementById('genderBtnMale'),
      genderBtnFemale: document.getElementById('genderBtnFemale'),
      startBtn: document.getElementById('startQuizBtn'),
      backBtn: document.getElementById('backBtn'),
      questionNumber: document.getElementById('questionNumber'),
      questionText: document.getElementById('questionText'),
      answersContainer: document.getElementById('answersContainer'),
      progressBar: document.getElementById('quizProgressBar'),
      progressFill: document.getElementById('quizProgressFill'),
      loadingMessage: document.getElementById('loadingMessage'),
      loadingProgressFill: document.getElementById('loadingProgressFill'),
      emailInput: document.getElementById('emailInput'),
      submitEmailBtn: document.getElementById('submitEmailBtn'),
      skipEmailBtn: document.getElementById('skipEmailBtn'),
      restartQuizBtn: document.getElementById('restartQuizBtn'),
      resultsSubtitle: document.getElementById('resultsSubtitle'),
      resultsPrimary: document.getElementById('resultsPrimary'),
      resultsIntensity: document.getElementById('resultsIntensity'),
      resultsScores: document.getElementById('resultsScores'),
      resultsResponses: document.getElementById('resultsResponses'),
      resultsJson: document.getElementById('resultsJson')
    };
  }

  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    // Gender selection buttons
    if (elements.genderBtnMale) {
      elements.genderBtnMale.addEventListener('click', function() {
        selectGender('male');
      });
    }
    if (elements.genderBtnFemale) {
      elements.genderBtnFemale.addEventListener('click', function() {
        selectGender('female');
      });
    }

    // Start quiz button
    if (elements.startBtn) {
      elements.startBtn.addEventListener('click', startQuiz);
    }

    // Back button
    if (elements.backBtn) {
      elements.backBtn.addEventListener('click', goBack);
    }

    // Browser back/forward buttons
    window.addEventListener('popstate', handlePopState);

    // Email form
    if (elements.submitEmailBtn) {
      elements.submitEmailBtn.addEventListener('click', submitEmail);
    }
    if (elements.skipEmailBtn) {
      elements.skipEmailBtn.addEventListener('click', skipEmail);
    }
    if (elements.emailInput) {
      elements.emailInput.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') {
          submitEmail();
        }
      });
    }

    // Restart quiz button
    if (elements.restartQuizBtn) {
      elements.restartQuizBtn.addEventListener('click', function() {
        sessionStorage.removeItem('quizState');
        window.location.reload();
      });
    }
  }

  /**
   * Handle gender selection
   */
  function selectGender(gender) {
    // Allow deselecting if clicking same button
    if (quizState.gender === gender) {
      quizState.gender = null;
      saveState();

      // Remove selection highlight
      if (elements.genderBtnMale && elements.genderBtnFemale) {
        elements.genderBtnMale.classList.remove('selected');
        elements.genderBtnFemale.classList.remove('selected');
      }

      // Disable start button
      if (elements.startBtn) {
        elements.startBtn.disabled = true;
      }
      return;
    }

    // Store gender in state
    quizState.gender = gender;
    saveState();

    // Update UI - highlight selected button
    if (elements.genderBtnMale && elements.genderBtnFemale) {
      elements.genderBtnMale.classList.remove('selected');
      elements.genderBtnFemale.classList.remove('selected');

      if (gender === 'male') {
        elements.genderBtnMale.classList.add('selected');
      } else {
        elements.genderBtnFemale.classList.add('selected');
      }
    }

    // Enable start button
    if (elements.startBtn) {
      elements.startBtn.disabled = false;
    }
  }

  /**
   * Get question for specific gender
   * Questions with gender flag are only shown to that gender
   */
  function getQuestionForGender(questionNumber, gender) {
    const allQuestions = window.quizConfig.questions;

    // Get all questions with this ID (there might be multiple for different genders)
    const questionsWithId = allQuestions.filter(q => q.id === questionNumber);

    if (questionsWithId.length === 0) {
      return null;
    }

    // If only one question with this ID, return it (neutral question)
    if (questionsWithId.length === 1 && !questionsWithId[0].gender) {
      return questionsWithId[0];
    }

    // Find question for this gender
    const genderQuestion = questionsWithId.find(q => q.gender === gender);
    if (genderQuestion) {
      return genderQuestion;
    }

    // Fallback to neutral question
    return questionsWithId.find(q => !q.gender) || questionsWithId[0];
  }

  /**
   * Filter answers based on gender
   * Answers with gender flag are only shown to that gender
   */
  function getFilteredAnswers(answers, gender) {
    if (!answers || !gender) {
      return answers;
    }

    return answers.filter(answer => {
      // If answer has no gender flag, show to everyone
      if (!answer.gender) {
        return true;
      }

      // If answer has gender flag, only show if it matches
      return answer.gender === gender;
    });
  }

  /**
   * Start the quiz
   */
  function startQuiz() {
    // Check if gender is selected
    if (!quizState.gender) {
      alert('Veuillez sélectionner votre sexe avant de commencer');
      return;
    }

    // Reset quiz state (but keep gender)
    quizState.startTime = Date.now();
    quizState.currentQuestion = 1;
    quizState.responses = [];
    quizState.selectedAnswers = [];
    quizState.completed = false; // Reset completion flag
    saveState();

    // Show question screen first, then render
    showScreen('question');

    // Use setTimeout to ensure screen is visible before rendering
    setTimeout(() => {
      renderQuestion(1);
      updateProgress();
    }, 50);

    // Push state for history API
    history.pushState(
      { screen: 'question', question: 1 },
      '',
      window.location.pathname + '?q=1'
    );
  }

  /**
   * Render a specific question
   */
  function renderQuestion(questionNumber) {
    // Get question based on gender
    const question = getQuestionForGender(questionNumber, quizState.gender);
    if (!question) {
      console.error('Question not found:', questionNumber);
      return;
    }

    // Clear selected answers for new question
    quizState.selectedAnswers = [];

    // Update question number and text
    elements.questionNumber.textContent = `Question ${questionNumber} sur ${quizState.totalQuestions}`;
    elements.questionText.textContent = question.text;

    // Add subtitle if present
    if (question.subtitle) {
      const existingSubtitle = elements.questionText.nextElementSibling;
      if (existingSubtitle && existingSubtitle.classList.contains('quiz-question-subtitle')) {
        existingSubtitle.textContent = question.subtitle;
      } else {
        const subtitle = document.createElement('p');
        subtitle.className = 'quiz-question-subtitle';
        subtitle.textContent = question.subtitle;
        elements.questionText.after(subtitle);
      }
    } else {
      // Remove subtitle if it exists
      const existingSubtitle = elements.questionText.nextElementSibling;
      if (existingSubtitle && existingSubtitle.classList.contains('quiz-question-subtitle')) {
        existingSubtitle.remove();
      }
    }

    // Clear previous answers
    elements.answersContainer.innerHTML = '';

    // Filter answers based on gender
    const filteredAnswers = getFilteredAnswers(question.answers, quizState.gender);

    // Render answer cards
    filteredAnswers.forEach(answer => {
      const card = createAnswerCard(question.id, answer, question.multiple);
      elements.answersContainer.appendChild(card);
    });

    // For multiple choice, add continue button
    if (question.multiple) {
      const continueBtn = document.createElement('button');
      continueBtn.className = 'quiz-btn quiz-btn-primary quiz-continue-btn';
      continueBtn.textContent = 'Continuer';
      continueBtn.style.marginTop = '24px';
      continueBtn.addEventListener('click', function() {
        handleMultipleChoiceContinue(question.id);
      });
      elements.answersContainer.appendChild(continueBtn);
    }

    // Always show back button (even on Q1 to return to welcome screen)
    if (elements.backBtn) {
      elements.backBtn.style.display = 'block';
    }

    // Update current question
    quizState.currentQuestion = questionNumber;
    saveState();
  }

  /**
   * Create an answer card element
   */
  function createAnswerCard(questionId, answer, isMultiple) {
    const card = document.createElement('div');
    card.className = 'quiz-answer-card';
    card.setAttribute('data-question-id', questionId);
    card.setAttribute('data-answer-id', answer.id);

    // Add multiple choice indicator
    if (isMultiple) {
      card.classList.add('quiz-answer-multiple');
      const checkbox = document.createElement('div');
      checkbox.className = 'quiz-answer-checkbox';
      card.appendChild(checkbox);
    }

    const iconContainer = document.createElement('div');
    iconContainer.className = 'quiz-answer-icon';

    // Support both image paths and emoji icons
    if (answer.image) {
      const img = document.createElement('img');
      img.src = answer.image;
      img.alt = answer.text;
      img.loading = 'lazy';
      iconContainer.appendChild(img);
    } else if (answer.icon) {
      iconContainer.textContent = answer.icon;
    }

    const text = document.createElement('div');
    text.className = 'quiz-answer-text';
    text.textContent = answer.text;

    card.appendChild(iconContainer);
    card.appendChild(text);

    // Add click handler
    card.addEventListener('click', function() {
      if (isMultiple) {
        handleMultipleChoiceClick(questionId, answer, card);
      } else {
        handleAnswerClick(questionId, answer);
      }
    });

    return card;
  }

  /**
   * Handle answer click (single choice)
   */
  function handleAnswerClick(questionId, answer) {
    // Prevent double-click
    if (quizState.isTransitioning) return;
    quizState.isTransitioning = true;

    // Find the clicked card
    const clickedCard = event.currentTarget;

    // Add selected state
    clickedCard.classList.add('selected');

    // Record the response
    recordResponse(questionId, [answer]);

    // Wait for visual feedback, then transition
    setTimeout(() => {
      if (quizState.currentQuestion < quizState.totalQuestions) {
        // Go to next question
        goToNextQuestion();
      } else {
        // Quiz complete - show email or loading
        completeQuiz();
      }
      quizState.isTransitioning = false;
    }, 300);
  }

  /**
   * Handle multiple choice click
   */
  function handleMultipleChoiceClick(questionId, answer, card) {
    // Toggle selection
    if (card.classList.contains('selected')) {
      card.classList.remove('selected');
      quizState.selectedAnswers = quizState.selectedAnswers.filter(a => a.id !== answer.id);
    } else {
      card.classList.add('selected');
      quizState.selectedAnswers.push(answer);
    }
  }

  /**
   * Handle continue button for multiple choice
   */
  function handleMultipleChoiceContinue(questionId) {
    // Check if at least one answer is selected
    if (quizState.selectedAnswers.length === 0) {
      alert('Veuillez sélectionner au moins une réponse');
      return;
    }

    // Prevent double-click
    if (quizState.isTransitioning) return;
    quizState.isTransitioning = true;

    // Record the responses
    recordResponse(questionId, quizState.selectedAnswers);

    // Wait for visual feedback, then transition
    setTimeout(() => {
      if (quizState.currentQuestion < quizState.totalQuestions) {
        // Go to next question
        goToNextQuestion();
      } else {
        // Quiz complete - show email or loading
        completeQuiz();
      }
      quizState.isTransitioning = false;
    }, 300);
  }

  /**
   * Record a response (handles both single and multiple answers)
   */
  function recordResponse(questionId, answers) {
    // Remove previous response(s) for this question (if going back and re-answering)
    quizState.responses = quizState.responses.filter(r => r.questionId !== questionId);

    // Ensure answers is an array
    const answersArray = Array.isArray(answers) ? answers : [answers];

    // Add new response(s)
    answersArray.forEach(answer => {
      quizState.responses.push({
        questionId: questionId,
        answerId: answer.id,
        text: answer.text,
        weights: answer.weights,
        urgency: answer.urgency || null,
        persona: answer.persona || null
      });
    });

    saveState();
  }

  /**
   * Go to next question
   */
  function goToNextQuestion() {
    const nextQuestion = quizState.currentQuestion + 1;

    // Fade out current question
    elements.questionScreen.classList.add('fade-out');

    setTimeout(() => {
      renderQuestion(nextQuestion);
      updateProgress();

      // Fade in next question
      elements.questionScreen.classList.remove('fade-out');

      // Push state for history API
      history.pushState(
        { screen: 'question', question: nextQuestion },
        '',
        window.location.pathname + '?q=' + nextQuestion
      );
    }, 200);
  }

  /**
   * Update welcome screen UI based on current state
   */
  function updateWelcomeScreenUI() {
    // Update gender button selection
    if (elements.genderBtnMale && elements.genderBtnFemale) {
      elements.genderBtnMale.classList.remove('selected');
      elements.genderBtnFemale.classList.remove('selected');

      if (quizState.gender === 'male') {
        elements.genderBtnMale.classList.add('selected');
      } else if (quizState.gender === 'female') {
        elements.genderBtnFemale.classList.add('selected');
      }
    }

    // Update start button state
    if (elements.startBtn) {
      elements.startBtn.disabled = !quizState.gender;
    }
  }

  /**
   * Go back to previous question or welcome screen
   */
  function goBack() {
    // If at question 1, return to welcome screen
    if (quizState.currentQuestion <= 1) {
      // Fade out question screen
      elements.questionScreen.classList.add('fade-out');

      setTimeout(() => {
        // Reset state (but keep gender selection)
        quizState.currentQuestion = 0;
        quizState.responses = [];
        quizState.selectedAnswers = [];
        saveState();

        // Show welcome screen and update its UI
        showScreen('welcome');
        updateWelcomeScreenUI();

        // Update history
        history.pushState(
          { screen: 'welcome' },
          '',
          window.location.pathname
        );
      }, 200);
      return;
    }

    const prevQuestion = quizState.currentQuestion - 1;

    // Fade out current question
    elements.questionScreen.classList.add('fade-out');

    setTimeout(() => {
      renderQuestion(prevQuestion);
      updateProgress();

      // Fade in previous question
      elements.questionScreen.classList.remove('fade-out');

      // Update history
      history.pushState(
        { screen: 'question', question: prevQuestion },
        '',
        window.location.pathname + '?q=' + prevQuestion
      );
    }, 200);
  }

  /**
   * Handle browser back/forward buttons
   */
  function handlePopState(event) {
    if (event.state) {
      if (event.state.screen === 'question' && event.state.question) {
        renderQuestion(event.state.question);
        updateProgress();
        showScreen('question');
      } else if (event.state.screen === 'welcome') {
        showScreen('welcome');
        updateWelcomeScreenUI();
      }
    }
  }

  /**
   * Update progress bar
   */
  function updateProgress() {
    const progress = (quizState.currentQuestion / quizState.totalQuestions) * 100;
    elements.progressFill.style.width = progress + '%';
  }

  /**
   * Complete the quiz
   */
  function completeQuiz() {
    // Check if email collection is enabled
    const collectEmail = elements.emailScreen &&
                        elements.emailScreen.parentElement.querySelector('.quiz-email') !== null;

    if (collectEmail) {
      showScreen('email');
    } else {
      processResults();
    }
  }

  /**
   * Submit email
   */
  function submitEmail() {
    const email = elements.emailInput.value.trim();

    // Basic email validation
    if (!email || !email.includes('@')) {
      alert('Veuillez entrer une adresse email valide');
      return;
    }

    quizState.email = email;
    saveState();
    processResults();
  }

  /**
   * Skip email
   */
  function skipEmail() {
    processResults();
  }

  /**
   * Process results and show loading screen
   */
  async function processResults() {
    showScreen('loading');

    // Calculate profile using scoring module
    const result = window.calculateProfile ?
                   window.calculateProfile(quizState.responses) :
                   { primary: 'hepatic', intensity: 'moderate', resultUrl: '/pages/metabolic-quiz-result?profile=hepatic&intensity=moderate' };

    // Start loading animation and get the total duration
    const totalDuration = animateLoadingScreen();

    // Create shareable result URL with base64-encoded data
    const resultData = {
      profile: result.primary,
      persona: result.persona,
      scores: result.scores,
      duration: result.duration,
      timestamp: Date.now()
    };

    const base64Data = btoa(JSON.stringify(resultData));
    const shareableUrl = '/pages/metabolic-quiz-result#' + base64Data;

    // Store in localStorage for "Your Recent Results" feature
    try {
      localStorage.setItem('quizResult_' + resultData.timestamp, JSON.stringify(resultData));
      console.log('✅ Result saved to localStorage');
    } catch (e) {
      console.warn('Could not save to localStorage:', e);
    }

    // Update result object with shareable URL
    result.shareableUrl = shareableUrl;

    // Submit to webhook (non-blocking)
    if (window.submitToWebhook) {
      const webhookData = {
        email: quizState.email,
        responses: quizState.responses,
        profile: result,
        resultUrl: result.resultUrl,
        shareableUrl: shareableUrl,
        duration: quizState.startTime ? Math.round((Date.now() - quizState.startTime) / 1000) : null
      };

      window.submitToWebhook(webhookData).catch(err => {
        console.error('Webhook submission failed:', err);
      });
    }

    // Wait for loading screen animation using the exact duration from animation
    await new Promise(resolve => setTimeout(resolve, totalDuration));

    // Redirect to shareable result page
    window.location.href = shareableUrl;
  }

  /**
   * Display results on screen
   */
  function displayResults(result) {
    // Mark quiz as completed
    quizState.completed = true;
    saveState();

    // Show results screen
    showScreen('results');

    // Set subtitle
    const profileName = window.quizConfig.profileNames[result.primary] || result.primary;
    const intensityName = window.quizConfig.intensityNames[result.intensity] || result.intensity;
    elements.resultsSubtitle.textContent = `Profil ${profileName} - Niveau ${intensityName}`;

    // Display primary profile
    elements.resultsPrimary.innerHTML = `
      <strong>Profil Principal:</strong> ${profileName}<br>
      <strong>Niveau d'Intensité:</strong> ${intensityName}<br>
      <strong>Durée du Protocole:</strong> ${result.duration} jours
    `;

    // Display intensity level and persona
    let intensityHtml = `<strong>URL Résultat:</strong> <a href="${result.resultUrl}" target="_blank">${result.resultUrl}</a>`;

    // Add persona if present
    if (result.persona && result.personaName) {
      intensityHtml += `<br><br><strong>Persona Marketing:</strong> ${result.personaName}`;
    }

    elements.resultsIntensity.innerHTML = intensityHtml;

    // Display scores breakdown
    let scoresHtml = '';
    for (const [profile, score] of Object.entries(result.scores)) {
      const name = window.quizConfig.profileNames[profile] || profile;
      scoresHtml += `
        <div class="results-score-item">
          <span class="results-score-label">${name}:</span>
          <span class="results-score-value">${score}</span>
        </div>
      `;
    }
    elements.resultsScores.innerHTML = scoresHtml;

    // Display responses
    let responsesHtml = '';
    quizState.responses.forEach((response, index) => {
      const question = window.quizConfig.questions[response.questionId - 1];
      responsesHtml += `
        <div class="results-response-item">
          <div class="results-response-question">
            <strong>Q${response.questionId}:</strong> ${question ? question.text : 'Question ' + response.questionId}
          </div>
          <div class="results-response-answer">
            <strong>Réponse:</strong> ${response.answerId} - ${response.text}
          </div>
        </div>
      `;
    });
    elements.resultsResponses.innerHTML = responsesHtml;

    // Display JSON dump
    const fullData = {
      email: quizState.email,
      responses: quizState.responses,
      profile: result,
      duration: quizState.startTime ? Math.round((Date.now() - quizState.startTime) / 1000) + 's' : null
    };
    elements.resultsJson.textContent = JSON.stringify(fullData, null, 2);
  }

  /**
   * Animate loading screen with smooth progressive animation
   * Supports individual durations per message
   * @returns {number} Total duration in milliseconds
   */
  function animateLoadingScreen() {
    const rawMessages = window.quizConfig.loadingMessages || [
      { text: "⏳ Analyse de vos réponses en cours...", duration: 4 },
      { text: "🧬 Identification de votre profil métabolique...", duration: 4 },
      { text: "🔍 Détection des blocages principaux...", duration: 4 },
      { text: "✅ Calcul de votre protocole personnalisé...", duration: 4 }
    ];

    const defaultDuration = window.quizConfig.loadingStepDuration || 4;

    // Normalize messages to objects {text, duration}
    const messages = rawMessages.map(msg => {
      if (typeof msg === 'string') {
        return { text: msg, duration: defaultDuration };
      }
      return { text: msg.text, duration: msg.duration || defaultDuration };
    });

    // Calculate total duration
    const totalDuration = messages.reduce((sum, msg) => sum + (msg.duration * 1000), 0);

    console.log('=== Loading Animation Started ===');
    console.log('Elements check:', {
      loadingProgressFill: elements.loadingProgressFill ? 'FOUND' : 'NOT FOUND',
      loadingMessage: elements.loadingMessage ? 'FOUND' : 'NOT FOUND'
    });

    // Verify element exists
    if (!elements.loadingProgressFill) {
      console.error('CRITICAL: loadingProgressFill element not found in DOM!');
      return totalDuration; // Return duration even if animation fails
    }

    // Reset progress bar to 0% at start
    elements.loadingProgressFill.style.width = '0%';

    // Set initial message
    let messageIndex = 0;
    elements.loadingMessage.textContent = messages[0].text;

    console.log('Animation settings:', {
      totalDuration: totalDuration + 'ms',
      messages: messages.map(m => `${m.text} (${m.duration}s)`)
    });

    // Schedule message changes at correct times
    let accumulatedTime = 0;
    messages.forEach((msg, index) => {
      if (index > 0) { // Skip first message (already set)
        accumulatedTime += messages[index - 1].duration * 1000;
        setTimeout(() => {
          messageIndex = index;
          elements.loadingMessage.textContent = msg.text;
          console.log(`Message changed to: ${msg.text} (${msg.duration}s)`);
        }, accumulatedTime);
      }
    });

    // Animate progress bar smoothly at 60fps
    const updateInterval = 16; // ~60fps
    const startTime = Date.now();
    let progress = 0;

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      progress = Math.min((elapsed / totalDuration) * 100, 100);

      elements.loadingProgressFill.style.width = progress.toFixed(2) + '%';

      if (progress >= 100) {
        clearInterval(progressInterval);
        console.log('=== Loading animation completed ===');
      }
    }, updateInterval);

    // Return total duration so caller knows how long to wait
    return totalDuration;
  }

  /**
   * Show specific screen
   */
  function showScreen(screenName) {
    const screens = {
      welcome: elements.welcomeScreen,
      question: elements.questionScreen,
      loading: elements.loadingScreen,
      email: elements.emailScreen,
      results: elements.resultsScreen
    };

    // Hide all screens and clean up transition classes
    Object.values(screens).forEach(screen => {
      if (screen) {
        screen.classList.remove('active');
        screen.classList.remove('fade-out'); // Clean up transition class
      }
    });

    // Show requested screen
    if (screens[screenName]) {
      screens[screenName].classList.add('active');
    }

    // Show/hide progress bar
    if (screenName === 'question') {
      elements.progressBar.style.display = 'block';
    } else {
      elements.progressBar.style.display = 'none';
    }
  }

  /**
   * Save state to sessionStorage
   */
  function saveState() {
    try {
      sessionStorage.setItem('quizState', JSON.stringify(quizState));
    } catch (e) {
      console.error('Failed to save state:', e);
    }
  }

  /**
   * Load state from sessionStorage
   */
  function loadState() {
    try {
      const saved = sessionStorage.getItem('quizState');
      if (saved) {
        const parsed = JSON.parse(saved);
        Object.assign(quizState, parsed);

        // If quiz was completed, don't resume - let user start fresh
        if (quizState.completed) {
          console.log('Quiz was previously completed. Ready for fresh start.');
          // Keep gender selection but reset to welcome screen
          quizState.currentQuestion = 0;
          quizState.responses = [];
          quizState.selectedAnswers = [];
          quizState.completed = false;
          saveState();
          updateWelcomeScreenUI();
          return;
        }

        // Resume quiz if in progress
        if (quizState.currentQuestion > 0 && quizState.currentQuestion <= quizState.totalQuestions) {
          showScreen('question');
          renderQuestion(quizState.currentQuestion);
          updateProgress();
        } else {
          // If on welcome screen, update UI to reflect saved state
          updateWelcomeScreenUI();
        }
      }
    } catch (e) {
      console.error('Failed to load state:', e);
    }
  }

  /**
   * Set viewport height for iOS Safari
   */
  function setViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  // Expose reset function for debugging
  window.resetQuiz = function() {
    sessionStorage.removeItem('quizState');
    window.location.reload();
  };

})();
